/**
 * Eine TimetableLessonGroup enthält alle Lerngruppen, die im Stundenplan gemeinsam in einem Feld angezeigt werden können.
 * Die Bedinguung dafür ist, dass alle diese Lerngruppen dieselbe viewHour und denselben span haben.
 * Soll beispielsweise ein zweistündiger Griechisch-Kurs angezeigt werden, bei dem in der 2. Stunde
 * aber die SuS einer Klasse fehlen, muss die Doppelstunde in zwei TimetableLessonGroups aufgeteilt werden.
 * 
 * 
 * 
 * Vorgehen:
 * - alle regulären Unterrichtsstunden ermitteln, ggf. splitten und in TimetableGroups unterbringen.
 *   -> die TimetableGroups legen für jeden Kurs eine TimetableCourse an.
 * - alle regulären Unterrichtsstunden ermitteln, ggf. splitten und in TimetableGroups unterbringen.
 *   -> dabei ggf. bestehende TimetableGroups splitten, wenn hour und span nicht übereinstimmen.
 *   -> die TimetableGroups ordnen Vertretungen, wenn möglich, einem bereits bestehenden TimetableCourse zu
 *      oder legen einen neuen TimetableCourse an.
 * 
 */

import { TimetableLessonGroup } from "./timetableLessonGroup";
import { TimetableSubstitution } from "./timetableSubstitution";
import { TimetableCourse } from "./timetableCourse";

export class TimetableManager {
   constructor(view, monday, weekNumber, mode, user, dm) {
      this.dm = dm;
      this.user = user;
      this.view = view;
      this.monday = monday;
      this.weekNumber = weekNumber;
      this.mode = mode;
      this.friday = new Date(monday);
      this.friday.setDate(monday.getDate() + 6);
   }

   async loadData() {

      // Regulären Stundenplan und Vertretungen der Woche ermitteln
      const regular = await this.getRegular();
      const substitutions = this.mode === 'regular' ? [] : await this.getSubstitutions();

      this.lessonGroups = [];

      // Reguläre Stunden in lessonGroups packen
      for (const item of regular) {
         const group = this.lessonGroups.find(g => g.weekday === item.weekday && g.hour === item.hour);
         if (group) {
            group.addCourse(item);
         }
         else {
            this.lessonGroups.push(new TimetableLessonGroup(item, this.view, this.mode, this.user, this.dm));
         }
      }

      //console.log(regular);
      //console.log(substitutions);
      //console.log(this.lessonGroups);

      // Vertretungen anwenden
      this.applySubstitutions(substitutions, this.lessonGroups);

   }

   render(elTable, gridPositions) {

      const usedCells = [];

      // LessonGroups rendern
      for (const lessonGroup of this.lessonGroups) {
         lessonGroup.render(elTable, gridPositions, this.dm);

         //usedCells.push({ weekday: item.weekday, hour: item.hour});
         for (let i=lessonGroup.hour; i<lessonGroup.hour + lessonGroup.span; ++i) usedCells.push({ weekday: lessonGroup.weekday, hour: i});

      }

      // leere Zellen
      for (let hour=1; hour<=10; ++hour) {
         for (let weekday=1; weekday<6; ++weekday) {
            if (!usedCells.find(l => l.weekday === weekday && l.hour === hour)) {
               const elEmpty = elTable.createElement('div', 'timetable-cell empty sel-' + weekday + '-' + hour);
               elEmpty.style.gridArea = `${gridPositions.hour[hour-1]} / ${gridPositions.weekday[weekday-1]} / span 1`;
            }
         }
      }


   }

   applySubstitutions(substitutions, lessonGroups) {

      for (const subst of substitutions) {

         let hours = subst.hours.slice();
         let addGroups = [];

         // Vertretung den bestehenden lessonGroups zuordnen, soweit möglich
         // -> dabei werden alle zugeordneten Stunden aus 'hours' entfernt
         for (const lessonGroup of lessonGroups) {
            const res = lessonGroup.apply(subst, hours);
            hours = res.hours;
            for (const add of res.addGroups) addGroups.push(add);
         }

         for (const add of addGroups) lessonGroups.push(add);

         // den Rest als eigene TimetableLessonGroup anlegen
         if (hours.length > 0) {
            const hourGroups = hours.groupByNeighbourhood();
            for (const hourGroup of hourGroups) {
               const newGroup = new TimetableLessonGroup(subst, this.view, this.mode, this.user, this.dm);
               newGroup.setHours(hourGroup);
               lessonGroups.push(newGroup);
            }

         }

      }


   }

   splitItemsByBreaks(items) {

      const breaks = [2,4,6];
      const newItems = [];

      for (const item of items) {
         const newSpans = [{hour: item.hour, span: item.span }];
   
         for (let hour=item.hour, span=item.span; hour < item.hour+item.span; ++hour, --span) {
            const br = breaks.find(b => b === hour);
            if (br && span > 1) {
               newSpans[newSpans.length-1].span = (br - newSpans[newSpans.length-1].hour) + 1;
               newSpans.push(JSON.parse(JSON.stringify(newSpans[newSpans.length-1])));
               newSpans[newSpans.length-1].hour = hour+1;
            }
         
         }

         if (newSpans.length === 1) {
            newItems.push(item);
         }
         else {
            for (const span of newSpans) {
               const newItem = JSON.parse(JSON.stringify(item));
               newItem.hour = span.hour;
               newItem.span = span.span;
               newItems.push(newItem);
            }
         }

      }

      return newItems;

   }

   async getRegular() {

      let items;

      if (this.view.type === 'teacher') {
         items = JSON.parse(JSON.stringify(await this.dm.getTimetableByTeacher(this.view.id)));
      }
      else if (this.view.type === 'pupil') {
         items = JSON.parse(JSON.stringify(await this.dm.getTimetableByPupil(this.view.id)));
      }
      else if (this.view.type === 'class') {
         items = JSON.parse(JSON.stringify(await this.dm.getTimetableByClass(this.view.id)));
      }
      else if (this.view.type === 'room') {
         items = await JSON.parse(JSON.stringify(this.dm.getTimetableByRoom(this.view.id)));
      }
      else if (this.view.type === 'course') {
         items = await JSON.parse(JSON.stringify(this.dm.getTimetableByCourse(this.view.id)));
      }


      items.map(item => {
         item.hour = item.displayHour;
         item.span = item.displaySpan;
         delete item.displayHour;
         delete item.displaySpan;
         const date = new Date(this.monday);
         date.setDate(this.monday.getDate() + (item.weekday-1));
         item.date = date.isoDate();


      });

      items = this.splitItemsByBreaks(items);
      //console.log(JSON.parse(JSON.stringify(items)));

      return items.map(item => new TimetableCourse(item, this.user, this.weekNumber));

   }

   async getSubstitutions() {
      let items = [];

      const dateRange = { from: this.monday.isoDate(), to: this.friday.isoDate() };

      if (this.view.type === 'teacher') {
         const subst = await this.dm.getSubstData(item => {
            return item.affectedTeachers.includes(this.view.short)
                && item.date >= dateRange.from && item.date <= dateRange.to
                && !item.absentPresence;
         }, (a,b) => 0);
         items = subst;
      }
      else if (this.view.type === 'pupil') {
         const courses = this.dm.getCoursesOfPupil(this.view.id).map(c => c.short);
         const subst = await this.dm.getSubstData(item => {
            if (item.date < dateRange.from || item.date > dateRange.to) return false;
            if (!item.origClasses.includes(this.view.classId)) return false;
            if (this.view.class.year >= 10 && !item.affectedTitles.hasIntersectionWith(courses)) return false;
            return true;
         }, (a,b) => 0);
         items = subst.map(item => {
            item.origClasses = item.origClasses.filter(cl => cl === this.view.classId);
            return item;
         });

      }
      else if (this.view.type === 'class') {
         const subst = await this.dm.getSubstData(item => {
            if (item.date < dateRange.from || item.date > dateRange.to) return false;
            if (!item.origClasses.includes(this.view.id)) return false;
            return true;
         }, (a,b) => 0);
         items = subst.map(item => {
            //item.origClasses = item.origClasses.filter(cl => cl === this.view.classId);
            return item;
         });
      }
      else if (this.view.type === 'room') {
         const subst = await this.dm.getSubstData(item => {
            return item.affectedRooms.includes(this.view.id)
                && item.date >= dateRange.from && item.date <= dateRange.to
                //&& !item.absentPresence;
         }, (a,b) => 0);
         items = subst;
      }
      else if (this.view.type === 'course') {
         const subst = await this.dm.getSubstData(item => {
            return item.affectedClasses.hasIntersectionWith(this.view.classes)
                && item.titles.includes(this.view.short)
                && item.date >= dateRange.from && item.date <= dateRange.to
         }, (a,b) => 0);
         items = subst;
      }

      items.map(item => {
         //console.log(item);
         //item.hour = item.hours[0];
         //item.span = item.hours.length;
         item.weekday = Date.fromString(item.date).getWeekday()+1;
         //item.rooms = item.rooms ? item.rooms.map(room => this.dm.getRoomByName(room).id) : [];
         //item.lessonRef = item.orig.lessonRef;
         //item.date = new Date.fromString(item.date);
      });

      items = this.splitItemsByBreaks(items);
      return items.map(item => new TimetableSubstitution(item, this.view, this.weekNumber, this.dm));

   }


}